import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
} from '@mui/material';
import { Code as CodeIcon, Tune as TuneIcon } from '@mui/icons-material';
import { SIMPLE_OPERATORS, getConditionableFields } from '../../utils/conditionUtils';
import VariablePicker from './VariablePicker';

/**
 * Reusable condition editor used by PropertiesPanel (visibility) and ScreenPanel (routing).
 *
 * Props:
 *   condition       – { mode, field, operator, value, expression } | null
 *   onChange        – (newCondition) => void
 *   screenComponents – components on the current screen (for field dropdown)
 *   label           – text shown before the field dropdown ("Show when" / "If")
 *   screens         – all screens (for VariablePicker in power mode)
 *   currentScreenId – current screen id (for VariablePicker)
 *   helperText      – plain-language explanation shown under the simple builder
 */
const ConditionBuilder = ({
  condition,
  onChange,
  screenComponents,
  label = 'Show when',
  screens,
  currentScreenId,
  helperText = 'This rule decides the outcome below; it applies only when the rule is true.',
}) => {
  const fields = getConditionableFields(screenComponents || []);
  const mode = condition?.mode ?? 'simple';
  const selectedField = fields.find(f => f.fieldName === condition?.field) || null;

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    onChange({ ...condition, mode: newMode });
  };

  return (
    <Box>
      {/* Mode toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
          <ToggleButton value="simple" sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem' }}>
            <TuneIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Simple
          </ToggleButton>
          <ToggleButton value="power" sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem' }}>
            <CodeIcon sx={{ fontSize: 14, mr: 0.5 }} />
            Expression
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {mode === 'simple' ? (
        <SimpleCondition
          label={label}
          condition={condition}
          onChange={onChange}
          fields={fields}
          selectedField={selectedField}
          helperText={helperText}
        />
      ) : (
        <PowerCondition
          condition={condition}
          onChange={onChange}
          screens={screens}
          currentScreenId={currentScreenId}
        />
      )}
    </Box>
  );
};

function SimpleCondition({ label, condition, onChange, fields, selectedField, helperText }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {label && (
          <Box sx={{ minWidth: 56, lineHeight: '40px', fontSize: '0.875rem', color: 'text.secondary' }}>
            {label}
          </Box>
        )}

        <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
          <InputLabel>When field</InputLabel>
          <Select
            value={condition?.field || ''}
            onChange={e => onChange({ ...condition, field: e.target.value, value: '' })}
            label="When field"
          >
            {fields.length === 0 && (
              <MenuItem value="" disabled>No fields yet</MenuItem>
            )}
            {fields.map(f => (
              <MenuItem key={f.fieldName} value={f.fieldName}>{f.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>is</InputLabel>
          <Select
            value={condition?.operator || 'eq'}
            onChange={e => onChange({ ...condition, operator: e.target.value })}
            label="is"
          >
            {SIMPLE_OPERATORS.map(op => (
              <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <ValueInput
          selectedField={selectedField}
          condition={condition}
          onChange={onChange}
        />
      </Box>

      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
}

function ValueInput({ selectedField, condition, onChange }) {
  if (!selectedField) {
    return (
      <TextField size="small" label="value" disabled sx={{ minWidth: 110, flex: 1 }} />
    );
  }

  if (selectedField.type === 'choice' && selectedField.dataSource?.length) {
    return (
      <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
        <InputLabel>value</InputLabel>
        <Select
          value={condition?.value || ''}
          onChange={e => onChange({ ...condition, value: e.target.value })}
          label="value"
        >
          {selectedField.dataSource.map(opt => (
            <MenuItem key={opt.id} value={opt.id}>{opt.title}</MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (selectedField.type === 'boolean') {
    return (
      <FormControl size="small" sx={{ minWidth: 110, flex: 1 }}>
        <InputLabel>value</InputLabel>
        <Select
          value={condition?.value || 'true'}
          onChange={e => onChange({ ...condition, value: e.target.value })}
          label="value"
        >
          <MenuItem value="true">Checked</MenuItem>
          <MenuItem value="false">Unchecked</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <TextField
      size="small"
      label="value"
      value={condition?.value || ''}
      onChange={e => onChange({ ...condition, value: e.target.value })}
      placeholder={selectedField.type === 'date' ? 'YYYY-MM-DD' : 'Enter value'}
      sx={{ minWidth: 110, flex: 1 }}
    />
  );
}

function PowerCondition({ condition, onChange, screens, currentScreenId }) {
  return (
    <Box>
      {screens && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <VariablePicker
            screens={screens}
            currentScreenId={currentScreenId}
            label="Insert field"
            onInsert={v => onChange({ ...condition, expression: (condition?.expression || '') + v })}
          />
        </Box>
      )}
      <TextField
        fullWidth
        size="small"
        multiline
        rows={2}
        placeholder="${form.field_name} == 'value'"
        value={condition?.expression || ''}
        onChange={e => onChange({ ...condition, expression: e.target.value })}
        helperText={
          <Box component="span" sx={{ display: 'block' }}>
            <Box component="span" sx={{ display: 'block' }}>
              Example: show when the customer picked &ldquo;premium&rdquo;, {' '}
              <Box component="span" sx={{ fontFamily: 'monospace' }}>
                {"${form.plan} == 'premium'"}
              </Box>
            </Box>
            {'Click "Insert field" to add a field instead of typing it. Operators: == != > < >= <= && ||'}
          </Box>
        }
        InputProps={{
          sx: { fontFamily: 'monospace', fontSize: '0.8rem' },
        }}
      />
    </Box>
  );
}

export default ConditionBuilder;
